import logError from 'src/data/services/logError';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { View, Image, Text, StyleSheet, TextInput, TouchableOpacity, Platform } from 'react-native';
import type { ImageStyle, TextStyle } from 'react-native';
import { SystemBars } from 'react-native-edge-to-edge';
import type { EdgeInsets } from 'react-native-safe-area-context';
import NetInfo, { type NetInfoState } from '@react-native-community/netinfo';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import { Ionicons } from '@expo/vector-icons';
import _ from 'lodash';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import EditButton from 'src/presentation/components/molecules/EditButton';
import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Translations from 'src/core/utils/Translations';
import Colors from 'src/core/utils/Colors';
import Constants from 'src/core/utils/Constants';
import Images from 'src/core/utils/Images';

const isIos = Platform.OS === 'ios';

interface NavBarProps {
	title?: string;
	bgcolor?: string;
	color?: string;
	bgImage?: string;
	backBtn?: boolean;
	searchBtn?: boolean;
	editBtn?: boolean;
	/** Anything a screen wants on the right of the bar instead of the edit button, say a favorite heart. */
	rightAction?: React.ReactNode;
	centerTitle?: boolean;
	backStep?: number;
	statusBarStyle?: string;
	iosPlaceholder?: string;
	onSearchTermChange?: (text: string) => void;
	onBackBtn?: () => void;
	insets?: EdgeInsets;
}

function NavBar({
	title,
	bgcolor,
	color,
	bgImage,
	backBtn,
	searchBtn,
	editBtn,
	rightAction,
	centerTitle,
	backStep,
	statusBarStyle,
	iosPlaceholder,
	onSearchTermChange,
	onBackBtn,
	insets,
}: NavBarProps) {
	const contentWidth = useContentWidth();
	const eventStore = useEventStore();
	const navigationStore = useNavigationStore();
	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const [term, setTerm] = useState('');
	const searchInputRef = useRef<TextInput>(null);

	const sizedStyles = useMemo(
		(): { navbarBgImage: ImageStyle } => ({
			navbarBgImage: {
				width: contentWidth,
			},
		}),
		[contentWidth],
	);

	const clearSearch = () => {
		setTerm('');
		onSearchTermChange?.('');
		searchInputRef.current?.focus();
	};

	// iOS heeft een gedebounced back-handler, Android een directe.
	const handleBackButtonRef = useRef(
		_.debounce(
			() => {
				if (onBackBtn) onBackBtn();
				if (backStep) {
					navigation.pop(backStep);
					return;
				}
				if (!_.isEmpty(eventStore?.previousTheme)) {
					eventStore?.resetTheme();
				}
				if (navigationStore?.notificationOrigin === 'selected') {
					navigation.navigate(Constants.screenIndex);
				} else {
					navigation.goBack();
				}
				navigationStore?.setNotificationOrigin('');
			},
			6000,
			{ leading: true, trailing: false },
		),
	);

	// Status-bar icon style per screen: light icons on dark headers, dark icons otherwise.
	const statusBarIconStyle = String(statusBarStyle ?? '').includes('light') ? 'light' : 'dark';

	useEffect(() => {
		const unsubscribe = NetInfo.addEventListener(handleConnectionChange);

		NetInfo.fetch()
			.then((state) => {
				// Treat unknown (null) as connected, same as handleConnectionChange below, so an unknown first reading
				// (common on the iOS simulator) does not show a false offline banner.
				navigationStore?.setConnectivityState(state.isConnected !== false);
			})
			.catch(logError);

		return () => unsubscribe();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleConnectionChange = (state: NetInfoState) => {
		// NetInfo sometimes reports `null` (unknown) during transitions; treat that optimistically as connected, so
		// we do not wrongly drop into offline mode (e.g.
		navigationStore?.setConnectivityState(state.isConnected !== false);
	};

	function handleAndroidBackButton() {
		if (!_.isEmpty(eventStore.previousTheme)) {
			eventStore.resetTheme();
		}

		if (navigationStore.notificationOrigin === 'selected') {
			navigation.navigate(Constants.screenIndex);
		} else if (backStep) {
			navigation.pop(backStep);
		} else {
			navigation.goBack();
		}
	}

	function titleStyle(titleColor: string): TextStyle {
		if (isIos) {
			return {
				color: titleColor,
				fontSize: Dimens.fontSize17,
				fontFamily: Fonts.montserratMedium,
				textAlign: 'center',
				alignSelf: 'center',
				paddingTop: Dimens.padding4,
				width: contentWidth - 180,
			};
		}
		return {
			color: titleColor,
			fontSize: Dimens.fontSize20,
			fontFamily: Fonts.montserratMedium,
			textAlign: centerTitle ? 'center' : 'left',
			alignSelf: 'center',
			width: centerTitle ? contentWidth - 140 : contentWidth - 76,
			paddingLeft: centerTitle ? 0 : 8,
		};
	}

	const rightSlot = rightAction ?? (editBtn === true ? <EditButton /> : null);

	// iOS: back button with label, search bar always visible under the header when searchBtn.
	if (isIos) {
		return (
			<View>
				<SystemBars style={{ statusBar: statusBarIconStyle, navigationBar: 'dark' }} />
				<View>
					{bgImage ? (
						<Image
							defaultSource={Images.placeholder}
							resizeMode="cover"
							source={{ uri: bgImage }}
							style={[styles.navbarBgImage, sizedStyles.navbarBgImage]}
						/>
					) : null}
					<View style={[styles.headerIos, { backgroundColor: bgcolor, paddingTop: insets?.top }]}>
						{backBtn ? (
							<View style={styles.left}>
								<TouchableOpacity style={styles.backBtn} onPress={() => handleBackButtonRef.current()}>
									<Ionicons color={color} name={'arrow-back'} size={24} />
								</TouchableOpacity>
							</View>
						) : (
							<View style={styles.left} />
						)}
						<View style={styles.body}>
							<Text numberOfLines={1} style={titleStyle(color!)}>
								{title ?? 'Topicus Events'}
							</Text>
						</View>
						<View style={styles.right}>{rightSlot}</View>
					</View>
					{searchBtn && onSearchTermChange && iosPlaceholder ? (
						<View style={[styles.searchBarContainer, { backgroundColor: bgcolor }]}>
							<View style={styles.searchInputRow}>
								<TouchableOpacity onPress={() => searchInputRef.current?.focus()}>
									<Ionicons color={Colors.greyPlatform} name="search" size={18} style={styles.searchIcon} />
								</TouchableOpacity>
								<TextInput
									placeholder={iosPlaceholder}
									placeholderTextColor={Colors.greyPlatform}
									ref={searchInputRef}
									style={styles.searchBar}
									value={term}
									onChangeText={(text) => {
										setTerm(text);
										onSearchTermChange(text);
									}}
								/>
								{term.length > 0 ? (
									<TouchableOpacity
										hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
										style={styles.clearIcon}
										onPress={clearSearch}
									>
										<Ionicons color={Colors.greyPlatform} name="close-circle" size={18} />
									</TouchableOpacity>
								) : null}
							</View>
						</View>
					) : null}
				</View>
			</View>
		);
	}

	// Android: back button as icon, search bar is toggled.
	return (
		<View style={[styles.androidRoot, { paddingTop: insets?.top, backgroundColor: bgcolor }]}>
			<SystemBars style={{ statusBar: statusBarIconStyle, navigationBar: 'dark' }} />
			{bgImage ? (
				<Image
					defaultSource={Images.placeholder}
					resizeMode="cover"
					source={{ uri: bgImage }}
					style={[styles.navbarBgImage, sizedStyles.navbarBgImage]}
				/>
			) : null}

			<View style={[styles.headerAndroid, { backgroundColor: bgcolor }]}>
				<View style={[styles.leftGroupAndroid, centerTitle ? styles.flex1 : null]}>
					{backBtn === true ? (
						<TouchableOpacity
							hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
							style={styles.backBtnAndroid}
							onPress={() => handleAndroidBackButton()}
						>
							<Ionicons name="arrow-back" size={24} style={{ color }} />
						</TouchableOpacity>
					) : null}
				</View>

				<View style={centerTitle ? StyleSheet.flatten(styles.centerTitle) : styles.flex1}>
					<Text numberOfLines={1} style={[titleStyle(color!), styles.titleAndroid]}>
						{title}
					</Text>
				</View>

				{centerTitle ? (
					<View style={[styles.titleWrapper, styles.flex1]}>{rightSlot}</View>
				) : rightSlot ? (
					<View style={styles.titleWrapper}>{rightSlot}</View>
				) : null}
			</View>

			{searchBtn === true && onSearchTermChange ? (
				<View style={[styles.searchBarContainer, { backgroundColor: bgcolor }]}>
					<View style={styles.searchInputRow}>
						<TouchableOpacity onPress={() => searchInputRef.current?.focus()}>
							<Ionicons color={Colors.greyPlatform} name="search" size={18} style={styles.searchIcon} />
						</TouchableOpacity>
						<TextInput
							placeholder={iosPlaceholder ?? Translations.placeholderSearch}
							placeholderTextColor={Colors.greyPlatform}
							ref={searchInputRef}
							style={styles.searchBar}
							value={term}
							onChangeText={(text) => {
								setTerm(text);
								onSearchTermChange(text);
							}}
						/>
						{term.length > 0 ? (
							<TouchableOpacity
								hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
								style={styles.clearIcon}
								onPress={clearSearch}
							>
								<Ionicons color={Colors.greyPlatform} name="close-circle" size={18} />
							</TouchableOpacity>
						) : null}
					</View>
				</View>
			) : null}
		</View>
	);
}

function NavBarWrapper(props: Omit<NavBarProps, 'insets'>) {
	const insets = useSafeAreaInsets();
	return <NavBar {...props} insets={insets} />;
}

export default NavBarWrapper;

const styles = StyleSheet.create({
	navbarBgImage: {
		position: 'absolute',
		top: 0,
	},
	flex1: {
		flex: 1,
	},

	// iOS
	headerIos: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingHorizontal: Dimens.padding10,
		paddingBottom: Dimens.padding16,
	},
	backBtn: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	searchBarContainer: {
		height: 52,
		width: '100%',
		paddingTop: 7,
		paddingBottom: 7,
		marginTop: -1,
	},
	searchInputRow: {
		flex: 1,
		flexDirection: 'row',
		alignItems: 'center',
		backgroundColor: Colors.white,
		borderRadius: 5,
		marginHorizontal: Dimens.margin8,
		paddingLeft: Dimens.padding12,
	},
	searchIcon: {
		marginRight: Dimens.margin6,
	},
	clearIcon: {
		paddingHorizontal: Dimens.padding8,
	},
	searchBar: {
		flex: 1,
		fontSize: Dimens.fontSize14,
		fontFamily: Fonts.montserratRegular,
		color: Colors.black,
		textAlign: 'left',
		paddingVertical: Dimens.padding8,
		paddingRight: Dimens.padding16,
	},
	left: {
		flex: 1,
	},
	body: {
		flex: 2,
		alignItems: 'center',
	},
	right: {
		flex: 1,
		alignItems: 'flex-end',
	},

	// Android
	androidRoot: {
		elevation: 5,
	},
	centerTitle: {
		flex: 1,
		justifyContent: 'center',
		alignItems: 'center',
	},
	headerAndroid: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		paddingVertical: Dimens.padding16,
		paddingHorizontal: Dimens.padding16,
	},
	leftGroupAndroid: {
		flexDirection: 'row',
		gap: Dimens.margin16,
	},
	titleWrapper: {
		zIndex: 10,
		alignItems: 'flex-end',
	},
	titleAndroid: {
		// The width from titleStyle() is a pixel value that does not know about the back button or the right
		// action, so on web it pushes them out of the bar. Let the row divide the space instead.
		width: 'auto',
		flexShrink: 1,
		includeFontPadding: false,
	},
	backBtnAndroid: {
		padding: Dimens.padding8,
	},
});
